import React, { useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';

import { useResizable } from '../../../Resizable';

import * as S from './styled';

function renderParagraph(paragraph) {
  const parts = paragraph.split(/\n/g);
  const result = [parts[0]];
  for (let i = 1; i < parts.length; i += 1) {
    result.push(<br key={`br-${i}`} />, parts[i]);
  }
  return result;
}

function renderComment(comment) {
  const paragraphs = comment
    .split(/\n\n/g)
    // eslint-disable-next-line react/no-array-index-key
    .map((p, i) => (
      <S.CommentParagraph key={`p-${i}`}>
        {renderParagraph(p)}
      </S.CommentParagraph>
    ));
  return paragraphs;
}

function renderFirstLine(comment) {
  const index = comment.indexOf('\n');
  if (index < 0) {
    return comment;
  }
  return comment.slice(0, index);
}

export default function CommentBlock({
  onResize,
  onExpand,
  isExpanded,
  comment = '',
}) {
  const stashRef = useRef(null);
  const compactRef = useRef(null);
  const buttonRef = useRef(null);

  const detectSizeDiff = () => {
    if (!stashRef.current || !compactRef.current || !buttonRef.current) {
      return;
    }

    const stash = stashRef.current.getBoundingClientRect();
    const compact = compactRef.current.getBoundingClientRect();

    if (compact.width <= stash.width || compact.height < stash.height) {
      buttonRef.current.style.display = 'inline-block';
    } else {
      buttonRef.current.style.display = 'none';
    }
  };

  const handleResize = (size) => {
    onResize?.(size);
    detectSizeDiff();
  };

  const ref = useRef();
  useResizable(ref, handleResize);

  useEffect(() => {
    detectSizeDiff();
  }, [comment]);

  const commentNodes = useMemo(() => renderComment(comment), [comment]);
  const commentStart = useMemo(() => renderFirstLine(comment), [comment]);

  return (
    <S.CommentBlock ref={ref}>
      <S.Stash>
        <S.CommentBody>
          <S.CommentText ref={stashRef}>{comment}</S.CommentText>
        </S.CommentBody>
        <S.CommentBody>
          <S.Compact ref={compactRef}>{commentStart}</S.Compact>
          <S.CommentControlls />
        </S.CommentBody>
      </S.Stash>
      <S.CommentBody>
        <S.Compact style={{ display: isExpanded ? 'none' : '' }}>
          {commentStart}
        </S.Compact>
        <S.Full style={{ display: isExpanded ? '' : 'none' }}>
          {commentNodes}
        </S.Full>
        <S.CommentControlls>
          <S.ExpandButton
            ref={buttonRef}
            onClick={() => {
              onExpand(!isExpanded);
            }}
          >
            …
          </S.ExpandButton>
        </S.CommentControlls>
      </S.CommentBody>
    </S.CommentBlock>
  );
}

CommentBlock.propTypes = {
  isExpanded: PropTypes.bool,
  onResize: PropTypes.func.isRequired,
  onExpand: PropTypes.func.isRequired,
  comment: PropTypes.string,
};
